import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, InputBase, ListSubheader, Menu, MenuItem, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { COLUMN, COMPOSER_INNER, COMPOSER_RING, FOCUS_RING } from './styles';
const PlusIcon = () => /*#__PURE__*/React.createElement("svg", {
  width: "20",
  height: "20",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "1.8",
  strokeLinecap: "round",
  "aria-hidden": true
}, /*#__PURE__*/React.createElement("path", {
  d: "M12 5v14M5 12h14"
}));
const ArrowUpIcon = () => /*#__PURE__*/React.createElement("svg", {
  width: "18",
  height: "18",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2.2",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true
}, /*#__PURE__*/React.createElement("path", {
  d: "M12 19V5M5 12l7-7 7 7"
}));
export function LivyComposer(_ref) {
  var _ringRef$current;
  let onSend = _ref.onSend,
    onStop = _ref.onStop,
    streaming = _ref.streaming,
    placeholder = _ref.placeholder,
    disclaimer = _ref.disclaimer,
    _ref$actions = _ref.actions,
    actions = _ref$actions === void 0 ? [] : _ref$actions,
    _ref$active = _ref.active,
    active = _ref$active === void 0 ? true : _ref$active;
  const _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    menuAnchor = _useState4[0],
    setMenuAnchor = _useState4[1];
  const ringRef = useRef(null);
  // The hook drops a send while a reply streams, so the menu closes (and + disables) for the duration.
  // It also closes when the surface is hidden, so it never floats over the page on its own.
  useEffect(() => {
    if (streaming || !active) setMenuAnchor(null);
  }, [streaming, active]);
  // Its side and height are measured on open, so a resize closes it rather than leaving it stale.
  useEffect(() => {
    if (!menuAnchor) return undefined;
    const close = () => setMenuAnchor(null);
    window.addEventListener('resize', close);
    return () => window.removeEventListener('resize', close);
  }, [menuAnchor]);
  // The menu never covers the box: below it while there is room (the opening screen), above it once
  // the box is docked at the bottom.
  const ringRect = menuAnchor ? (_ringRef$current = ringRef.current) === null || _ringRef$current === void 0 ? void 0 : _ringRef$current.getBoundingClientRect() : undefined;
  const below = ringRect ? window.innerHeight - ringRect.bottom : 0;
  const opensUp = ringRect ? below < 280 && ringRect.top > below : false;
  const room = ringRect ? (opensUp ? ringRect.top : window.innerHeight - ringRect.bottom) - 24 : 440;
  const inputRef = useRef(null);
  const ready = value.trim().length > 0 && !streaming;
  const submit = () => {
    if (!ready) return;
    onSend(value);
    setValue('');
  };
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      px: {
        xs: '16px',
        sm: '24px'
      },
      pb: '14px',
      pt: '4px'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: COLUMN
  }, /*#__PURE__*/React.createElement(Box, {
    sx: COMPOSER_RING(streaming),
    ref: ringRef
  }, /*#__PURE__*/React.createElement(Box, {
    sx: COMPOSER_INNER,
    onClick: () => {
      var _inputRef$current;
      return (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
    }
  }, actions.length > 0 && /*#__PURE__*/React.createElement(IconButton, {
    "aria-label": "More options",
    "aria-haspopup": "menu",
    disabled: streaming,
    onClick: e => {
      e.stopPropagation();
      setMenuAnchor(e.currentTarget);
    },
    sx: _objectSpread(_objectSpread({}, FOCUS_RING), {}, {
      width: 40,
      height: 40,
      flex: '0 0 auto',
      color: 'text.primary',
      transition: 'color .2s, background-color .2s, transform .25s',
      '&:hover': {
        color: 'primary.main',
        bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
      },
      '&.Mui-disabled': {
        opacity: 0.4
      }
    }, menuAnchor && {
      transform: 'rotate(45deg)',
      color: 'primary.main'
    })
  }, /*#__PURE__*/React.createElement(PlusIcon, null)), /*#__PURE__*/React.createElement(InputBase, {
    multiline: true,
    maxRows: 8,
    value: value,
    inputRef: inputRef,
    onChange: e => setValue(e.target.value),
    placeholder: streaming ? 'Livy is thinking…' : placeholder,
    inputProps: {
      'aria-label': 'Message Livy'
    },
    sx: {
      flex: 1,
      fontSize: 16,
      lineHeight: 1.5,
      py: '8px',
      pl: actions.length > 0 ? 0 : '10px'
    },
    onKeyDown: e => {
      // Enter sends; Shift-Enter is a newline, which is what people expect of a chat box.
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    }
  }), /*#__PURE__*/React.createElement(IconButton, {
    "aria-label": streaming ? 'Stop' : 'Send',
    onClick: e => {
      e.stopPropagation();
      if (streaming) onStop();else submit();
    },
    disabled: !streaming && !ready,
    sx: _objectSpread(_objectSpread({}, FOCUS_RING), {}, {
      width: 40,
      height: 40,
      flex: '0 0 auto',
      color: '#fff',
      bgcolor: streaming ? 'text.primary' : 'primary.main',
      transition: 'background-color .2s, opacity .2s, transform .15s',
      '&:hover': {
        bgcolor: streaming ? 'text.secondary' : 'primary.dark'
      },
      '&:active': {
        transform: 'scale(.94)'
      },
      // Still the brand button when there is nothing to send, just quieter.
      '&.Mui-disabled': {
        bgcolor: 'primary.main',
        color: '#fff',
        opacity: 0.35
      }
    })
  }, streaming ? /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 12,
      height: 12,
      borderRadius: '3px',
      bgcolor: 'background.paper'
    }
  }) : /*#__PURE__*/React.createElement(ArrowUpIcon, null)))), disclaimer && /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary",
    sx: {
      display: 'block',
      textAlign: 'center',
      mt: '8px',
      fontSize: 11,
      opacity: 0.75
    }
  }, disclaimer)), /*#__PURE__*/React.createElement(Menu, {
    anchorEl: ringRef.current,
    open: Boolean(menuAnchor),
    onClose: () => setMenuAnchor(null),
    anchorOrigin: {
      vertical: opensUp ? 'top' : 'bottom',
      horizontal: 'left'
    },
    transformOrigin: {
      vertical: opensUp ? 'bottom' : 'top',
      horizontal: 'left'
    },
    slotProps: {
      paper: {
        sx: {
          my: '8px',
          minWidth: 300,
          maxWidth: 520,
          maxHeight: Math.min(440, room),
          borderRadius: '16px',
          border: 1,
          borderColor: 'divider',
          boxShadow: '0 16px 40px rgba(0,0,0,.24)'
        }
      }
    }
  }, actions.flatMap((a, i) => {
    var _actions, _a$group;
    return [...(a.group && a.group !== ((_actions = actions[i - 1]) === null || _actions === void 0 ? void 0 : _actions.group) ? [/*#__PURE__*/React.createElement(ListSubheader, {
      key: "g-".concat(i, "-").concat(a.group),
      sx: {
        lineHeight: '30px',
        fontSize: 11,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        bgcolor: 'background.paper'
      }
    }, a.group)] : []), /*#__PURE__*/React.createElement(MenuItem, {
      key: "".concat((_a$group = a.group) !== null && _a$group !== void 0 ? _a$group : '', "-").concat(a.label),
      onClick: () => {
        setMenuAnchor(null);
        a.onClick();
      },
      sx: {
        gap: '10px',
        py: '8px',
        mx: '6px',
        borderRadius: '10px',
        fontSize: 14,
        whiteSpace: 'normal'
      }
    }, /*#__PURE__*/React.createElement(Box, {
      component: "span"
    }, a.label), a.hint && /*#__PURE__*/React.createElement(Box, {
      component: "span",
      sx: {
        color: 'text.secondary',
        fontSize: 13,
        ml: 'auto'
      }
    }, a.hint))];
  })));
}